import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

const categoryOptions = [
  { value: 'ongoing', label: 'Devam Eden' },
  { value: 'completed', label: 'Tamamlanan' },
  { value: 'future', label: 'Gelecek' }
];

export default function ProjectManager() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: '', slug: '', subtitle: '', description: '',
    category: 'ongoing', position: 0, is_featured: false, is_active: true
  });
  const [coverFile, setCoverFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filterCat, setFilterCat] = useState('');

  const fetchProjects = useCallback(() => {
    api.get('/projects/all').then(r => setProjects(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchProjects(); }, [fetchProjects]);

  const resetForm = () => {
    setForm({ title: '', slug: '', subtitle: '', description: '', category: 'ongoing', position: 0, is_featured: false, is_active: true });
    setCoverFile(null);
    setGalleryFiles([]);
    setGalleryImages([]);
    setEditing(null);
    setShowForm(false);
  };

  const parseImages = (imgs) => {
    if (Array.isArray(imgs)) return imgs;
    if (typeof imgs === 'string') { try { const p = JSON.parse(imgs || '[]'); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  };

  const handleEdit = (project) => {
    setEditing(project);
    setForm({
      title: project.title || '',
      slug: project.slug || '',
      subtitle: project.subtitle || '',
      description: project.description || '',
      category: project.category || 'ongoing',
      position: project.position || 0,
      is_featured: project.is_featured || false,
      is_active: project.is_active !== false
    });
    setGalleryImages(parseImages(project.images));
    setGalleryFiles([]);
    setShowForm(true);
  };

  const removeGalleryImage = (idx) => {
    setGalleryImages(imgs => imgs.filter((_, i) => i !== idx));
  };

  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await api.post('/uploads', fd);
    return res.data.path;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Başlık gerekli'); return; }
    setIsSubmitting(true);
    try {
      let coverImage = editing?.cover_image || '';
      if (coverFile) {
        coverImage = await uploadFile(coverFile);
      }

      let existingImages = galleryImages;
      let newImages = [];
      for (const file of galleryFiles) {
        const path = await uploadFile(file);
        newImages.push(path);
      }

      const payload = {
        ...form,
        cover_image: coverImage,
        images: [...existingImages, ...newImages]
      };

      if (editing) {
        await api.put(`/projects/${editing.id}`, payload);
        toast.success('Proje güncellendi');
      } else {
        await api.post('/projects', payload);
        toast.success('Proje eklendi');
      }

      // Physically delete removed images from R2 storage
      if (editing) {
        const originalImages = parseImages(editing.images);
        const toDelete = originalImages.filter(p => p && !galleryImages.includes(p));
        // Old cover replaced with a new one
        if (coverFile && editing.cover_image && editing.cover_image !== coverImage) {
          toDelete.push(editing.cover_image);
        }
        if (toDelete.length > 0) {
          try { await api.post('/uploads/delete', { paths: toDelete }); } catch (_) { /* non-blocking */ }
        }
      }

      resetForm();
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Silmek istediğinize emin misiniz?')) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success('Proje silindi');
      fetchProjects();
    } catch (err) {
      toast.error('Silme hatası');
    }
  };

  const filtered = filterCat ? projects.filter(p => p.category === filterCat) : projects;

  return (
    <div data-testid="admin-project-manager">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-semibold">Proje Yönetimi</h1>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] active:scale-[0.98] transition-colors"
          data-testid="admin-project-create-button"
        >
          <Plus className="w-4 h-4" /> Yeni Proje
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">{editing ? 'Proje Düzenle' : 'Yeni Proje'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <InputField label="Başlık" value={form.title} onChange={v => setForm(f => ({ ...f, title: v }))} required />
              <InputField label="Slug" value={form.slug} onChange={v => setForm(f => ({ ...f, slug: v }))} placeholder="otomatik oluşturulur" />
              <InputField label="Alt Başlık" value={form.subtitle} onChange={v => setForm(f => ({ ...f, subtitle: v }))} />
              <div>
                <label className="block text-sm font-medium mb-1.5">Kategori</label>
                <select
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                >
                  {categoryOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <InputField label="Sıra" type="number" value={form.position} onChange={v => setForm(f => ({ ...f, position: parseInt(v) || 0 }))} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5">Açıklama</label>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                rows={4}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))] resize-none"
              />
            </div>

            {/* Cover Image */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Kapak Görseli</label>
              <div className="flex items-center gap-4">
                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary transition-colors">
                  <Upload className="w-4 h-4" /> Görsel Seç
                  <input type="file" accept="image/*" className="hidden" onChange={e => setCoverFile(e.target.files[0])} />
                </label>
                <span className="text-sm text-muted-foreground">
                  {coverFile ? coverFile.name : (editing?.cover_image ? 'Mevcut görsel yüklü' : 'Seçilmedi')}
                </span>
              </div>
              {editing?.cover_image && <img src={getFileUrl(editing.cover_image)} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
            </div>

            {/* Gallery */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Galeri Görselleri</label>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary transition-colors">
                <Upload className="w-4 h-4" /> Görsel Ekle
                <input type="file" accept="image/*" multiple className="hidden" onChange={e => setGalleryFiles(Array.from(e.target.files))} />
              </label>
              {galleryFiles.length > 0 && <p className="text-xs text-muted-foreground mt-1">{galleryFiles.length} yeni dosya seçildi (kaydedince eklenecek)</p>}
              {galleryImages.length > 0 && (
                <div className="flex gap-3 mt-3 flex-wrap" data-testid="gallery-images-grid">
                  {galleryImages.map((img, i) => (
                    <div key={i} className="relative group" data-testid={`gallery-image-${i}`}>
                      <img src={getFileUrl(img)} alt="" className="h-16 w-16 rounded-lg object-cover border border-border" />
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(i)}
                        className="absolute -top-1.5 -right-1.5 p-1 rounded-full bg-destructive text-white shadow-md opacity-90 hover:opacity-100 active:scale-95 transition-opacity"
                        title="Görseli kaldır"
                        aria-label="Görseli kaldır"
                        data-testid={`gallery-remove-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_featured} onChange={e => setForm(f => ({ ...f, is_featured: e.target.checked }))} className="rounded" />
                Öne Çıkan
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
                Aktif
              </label>
            </div>

            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50" data-testid="admin-save-button">
                {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
              <button type="button" onClick={resetForm} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        <button onClick={() => setFilterCat('')} className={`px-3 py-1.5 text-xs rounded-lg ${!filterCat ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'bg-secondary'}`}>Tumu</button>
        {categoryOptions.map(o => (
          <button key={o.value} onClick={() => setFilterCat(o.value)} className={`px-3 py-1.5 text-xs rounded-lg ${filterCat === o.value ? 'bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))]' : 'bg-secondary'}`}>{o.label}</button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-10"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground">Proje bulunamadı</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(project => (
            <div key={project.id} className="flex items-center gap-4 p-4 rounded-xl bg-card border border-border">
              <div className="w-20 h-14 rounded-lg overflow-hidden bg-secondary shrink-0">
                {project.cover_image && <img src={getFileUrl(project.cover_image)} alt="" className="w-full h-full object-cover" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{project.title}</div>
                <div className="text-xs text-muted-foreground">{project.subtitle}</div>
              </div>
              <span className="px-2 py-0.5 text-xs rounded-full bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))]">
                {categoryOptions.find(o => o.value === project.category)?.label}
              </span>
              {project.is_featured && <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Öne Çıkan</span>}
              <div className="flex gap-1">
                <button onClick={() => handleEdit(project)} className="p-2 rounded-lg hover:bg-secondary"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(project.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const InputField = ({ label, value, onChange, type = 'text', placeholder, required }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5">{label} {required && <span className="text-destructive">*</span>}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
    />
  </div>
);
